import { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Badge, Boton, Campo, Input } from "@/components/ui";
import { Combobox, Dinero, HojaInferior } from "@/components/ui/controles";
import { BUCKET_ICONO } from "@/components/charts";
import type { CatalogAsset } from "@/engine/catalog/types";
import type { InvestableBucket } from "@/engine/types";
import { activoPorSub, ayudaValor, CAT, CATEGORIAS, COMUNES, subcategoriasDe } from "../catalogo-ui";

export interface ActivoElegido { what: string; category: string; subcategory: string; value: number | null }

export function ChipBucket({ a }: { a: CatalogAsset }) {
  const inv = a.investable === "Sí" && a.bucket !== "Proyectos" && a.bucket !== "Contribución";
  return (
    <span className="flex flex-wrap items-center gap-1">
      {inv ? <Badge tono="neutro">{BUCKET_ICONO[a.bucket as InvestableBucket]} {a.bucket}</Badge> : <Badge tono="neutro">Fuera del portafolio</Badge>}
      <Badge tono={inv ? "ok" : "neutro"}>{inv ? "Cuenta como invertible" : "No se rebalancea"}</Badge>
      <span className="text-xs text-tinta-suave">Liquidez {a.liquidity.toLowerCase()}</span>
    </span>
  );
}

/** Agregar o editar un activo en 3 pasos: tipo → subcategoría → nombre y valor. Siempre guarda el texto canónico del catálogo. */
export function SelectorActivo({ abierto, onCerrar, inicial, onGuardar, moneda }: {
  abierto: boolean; onCerrar: () => void; inicial?: ActivoElegido | null; onGuardar: (a: ActivoElegido) => void; moneda: string;
}) {
  const [paso, setPaso] = useState<1 | 2 | 3>(1);
  const [cat, setCat] = useState<string | null>(null);
  const [sub, setSub] = useState<CatalogAsset | null>(null);
  const [what, setWhat] = useState("");
  const [valor, setValor] = useState<number | null>(null);
  useEffect(() => {
    if (!abierto) return;
    const a = inicial ? activoPorSub(inicial.subcategory) : undefined;
    setCat(a?.category ?? null); setSub(a ?? null); setWhat(inicial?.what ?? ""); setValor(inicial?.value ?? null);
    setPaso(a ? 3 : 1);
  }, [abierto, inicial]);
  const items = useMemo(() => CAT.assets.map((a) => ({ clave: a.subcategory, etiqueta: a.subcategory, secundaria: `${CATEGORIAS.find((c) => c.canonica === a.category)?.llana ?? a.category} · ${a.examples}`, buscar: `${a.category} ${a.examples}` })), []);
  const elegirSub = (s: string) => { const a = activoPorSub(s); if (!a) return; setCat(a.category); setSub(a); if (!what || items.some((i) => i.clave === what)) setWhat(a.subcategory); setPaso(3); };
  const titulo = paso === 1 ? "¿Qué tipo de cosa es?" : paso === 2 ? (CATEGORIAS.find((c) => c.canonica === cat)?.llana ?? "Elige") : inicial ? "Editar" : "Nombre y valor";
  return (
    <HojaInferior abierta={abierto} onCerrar={onCerrar} titulo={titulo}
      pie={paso === 3 && sub ? <Boton className="w-full" disabled={valor === null} onClick={() => { onGuardar({ what: what.trim() || sub.subcategory, category: sub.category, subcategory: sub.subcategory, value: valor }); onCerrar(); }}>{inicial ? "Guardar" : "Agregar"}</Boton> : undefined}>
      {paso === 1 && (
        <div className="flex flex-col gap-4">
          <Combobox etiqueta="Buscar tipo de activo" placeholder="Busca: GBM, Bitso, AFORE, casa, coche…" items={items} onElegir={elegirSub} />
          <div>
            <p className="mb-2 text-sm font-medium text-tinta-suave">Lo más común</p>
            <div className="flex flex-wrap gap-2">{COMUNES.map((c) => <button key={c.sub} type="button" onClick={() => elegirSub(c.sub)} className="min-h-11 rounded-full border border-borde bg-white px-4 text-sm font-medium hover:border-primary">{c.etiqueta}</button>)}</div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {CATEGORIAS.map((c) => (
              <button key={c.canonica} type="button" onClick={() => { setCat(c.canonica); setPaso(2); }} className="flex min-h-16 flex-col items-start rounded-md border border-borde bg-white p-3 text-left hover:border-primary">
                <span className="font-semibold">{c.llana}</span>
                <span className="text-xs text-tinta-suave">{c.linea}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {paso === 2 && cat && (
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => setPaso(1)} className="mb-1 inline-flex min-h-11 items-center gap-1 self-start text-sm text-tinta-suave"><ArrowLeft className="h-4 w-4" aria-hidden /> Otro tipo</button>
          <p className="text-xs text-tinta-suave">{cat}</p>
          {subcategoriasDe(cat).map((a) => (
            <button key={a.subcategory} type="button" onClick={() => elegirSub(a.subcategory)} className="flex flex-col gap-1.5 rounded-md border border-borde bg-white p-3 text-left hover:border-primary">
              <span className="font-medium">{a.subcategory}</span>
              {a.examples && <span className="text-xs text-tinta-suave">{a.examples}</span>}
              <ChipBucket a={a} />
            </button>
          ))}
        </div>
      )}
      {paso === 3 && sub && (
        <div className="flex flex-col gap-4">
          <button type="button" onClick={() => setPaso(2)} className="inline-flex min-h-11 items-center gap-1 self-start text-sm text-tinta-suave"><ArrowLeft className="h-4 w-4" aria-hidden /> Cambiar tipo</button>
          <div className="rounded-md border border-borde bg-white p-3"><p className="font-medium">{sub.subcategory}</p><p className="mb-2 text-xs text-tinta-suave">{sub.category}</p><ChipBucket a={sub} /></div>
          <Campo etiqueta="¿Qué es? (para que lo reconozcas)" htmlFor="what"><Input id="what" value={what} onChange={(e) => setWhat(e.target.value)} placeholder="Ej.: BBVA nómina, portafolio en GBM" /></Campo>
          <Campo etiqueta="¿Cuánto vale hoy?" htmlFor="valor" ayuda={ayudaValor(sub)}><Dinero id="valor" valor={valor} onChange={setValor} moneda={moneda} tamano="lg" /></Campo>
        </div>
      )}
    </HojaInferior>
  );
}
